import { Endpoints } from '@/remote/endpoints'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/view/components/ui/table'
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table'
import { Eye, Image, Trash2, UserPen } from 'lucide-react'
import * as React from 'react'
import { DialogDelete } from '../modals/modal-delete'
import { DialogDetailPlans } from '../modals/modal-detail-plans'
import { DialogEditPlan } from '../modals/modal-edit-plan'
import { convertDateDisplayFormat } from '../utils/formatDateDisplay'
import { PlanFormData } from '../validators/plan-validator'
import { ScrollArea } from './ui/scroll-area'

interface Props {
  data: PlanFormData[]
  getRowsSelecteds?: (data: any) => void
  getRefreshList?: () => void
}

export function DataTablePlans({ data, getRefreshList }: Props) {
  const columns: ColumnDef<PlanFormData>[] = [
    {
      id: 'select',
      header: '',
      cell: () => <Image />,
      enableSorting: false,
      enableHiding: false
    },
    {
      accessorKey: 'nome',
      header: 'Nome',
      cell: ({ row }) => <div className="capitalize">{row.getValue('nome')}</div>
    },
    {
      accessorKey: 'dataVencimento',
      header: 'Data de Vencimento',
      cell: ({ row }) => {
        const formatDate = (Date: string) => {
          const format = Date.split('T')[0] || ''
          return convertDateDisplayFormat(format)
        }

        const formattedBirthDate = formatDate(row.getValue('dataVencimento') || '')

        return <div className="lowercase">{formattedBirthDate}</div>
      }
    },
    {
      id: 'actions',
      enableHiding: false,
      cell: ({ row }) => {
        const plan = row.original;
        const [dialogDelete, setDialogDelete] = React.useState(false);
        const [dialogDetail, setDialogDetail] = React.useState(false);
        const [dialogEdit, setDialogEdit] = React.useState(false);

        const closeModalDelete = () => setDialogDelete(false);
        const closeModelDetail = () => setDialogDetail(false);
        const closeModalEdit = () => setDialogEdit(false);

        return (
          <div className="flex items-center gap-3">
            <DialogDelete
              isOpen={dialogDelete}
              closeDialog={closeModalDelete}
              id={plan?.id || 0}
              url={Endpoints.plans}
              label="Excluir Plano"
              name={plan?.nome}
              updateList={getRefreshList}
            />
            <DialogEditPlan
              isOpen={dialogEdit}
              closeDialog={closeModalEdit}
              id={plan.id || 0}
              updateList={getRefreshList}
            />
            <DialogDetailPlans
              isOpen={dialogDetail}
              closeDialog={closeModelDetail}
              id={plan?.id || 0}
            />

            <Eye
              height={24}
              width={30}
              onClick={() => setDialogDetail(true)}
              className="text-blue-500 cursor-pointer p-1 hover:text-blue-400"
            />
            <UserPen
              height={24}
              width={30}
              onClick={() => setDialogEdit(true)}
              className="text-blue-500 cursor-pointer p-1 hover:text-blue-400"
            />
            <Trash2
              height={24}
              width={30}
              onClick={() => setDialogDelete(true)}
              className="text-red-500 cursor-pointer p-1 hover:text-red-400"
            />
          </div>
        );
      }

    }
  ]

  const [sorting, setSorting] = React.useState<SortingState>([])
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([])
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({})
  const [rowSelection, setRowSelection] = React.useState({})

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    initialState: { pagination: { pageSize: 100 } },
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection
    }
  })

  return (
    <div className="w-full">
      <div className="rounded-md border min-w-full">
        <ScrollArea className="m-4 h-container-members-list relative">
          <Table className="border-none">
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return <TableHead key={header.id}>{header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}</TableHead>
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length} className="h-24 text-center"></TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </ScrollArea>
      </div>
    </div>
  )
}
